/**
 * Jovii — SOURCE PROVIDERS: where the bytes legally come from.
 *
 * The owner's plan is commercial: Real-Debrid is one source among what will be
 * several — "the plan will be to add Netflix and other things in a legal manner".
 * So a provider is a first-class concept the picker renders as a bubble, not a
 * hardcoded label, even while there is exactly one.
 *
 * NOT the same thing as `Candidate.provider`. That field names the SCRAPER that
 * found the release (torrentio, mediafusion) — a search engine, not a source.
 * Every candidate the service returns today streams from Real-Debrid, whichever
 * scraper found it; the scrapers stay available as a filter INSIDE the provider.
 *
 * When a second provider lands, `sourceOf` is the one place that learns to tell
 * its candidates apart (a `Candidate.source` field, a URL shape — whatever the
 * service ships), and the bubbles, counts and filtering all follow.
 */

import type { Candidate } from '$types';

export interface SourceProvider {
  /** Stable id; the bubble-selection key. */
  id: string;
  /** What the bubble says. */
  label: string;
  /**
   * How the provider hands out streams. 'debrid' resolves scraped releases (the
   * picker shows a release list to verify); 'direct' would be a licensed service
   * with exactly one stream per title and no release list at all.
   */
  kind: 'debrid' | 'direct';
}

export const REAL_DEBRID: SourceProvider = {
  id: 'real-debrid',
  label: 'Real-Debrid',
  kind: 'debrid'
};

/** The second store, live since docs/TORBOX-TRIAL.md. Same shape, different cache:
 *  1 500 releases Real-Debrid does not have, and its own CDN. */
export const TORBOX: SourceProvider = {
  id: 'torbox',
  label: 'TorBox',
  kind: 'debrid'
};

/**
 * The THIRD store, live since docs/PREMIUMIZE-TRIAL.md. Its cache is the fullest of
 * the four ever measured and its claims converted 33/33 — but every gigabyte it serves
 * costs a point from a 1 000-point monthly budget, which is why `rank.ts` treats it as
 * a last resort rather than a peer. See `WEIGHTS.storeLastResort`.
 */
export const PREMIUMIZE: SourceProvider = {
  id: 'premiumize',
  label: 'Premiumize',
  kind: 'debrid'
};

/**
 * Which provider serves this candidate's bytes.
 *
 * THE SERVICE TOKEN IS ALREADY IN `originLabel`. `internal/catalog/parse.ts` builds it
 * as `addon · tracker · service` from AIOStreams' `bingeGroup`, where the service seat
 * is `RD` for Real-Debrid, `TORBOX` for TorBox and `PREMIUMIZE` for Premiumize — so a
 * second and third store needed no new wire field, only for this function to stop
 * assuming. Mirrors `parseStore` in `internal/catalog/parse.ts`; both are tested.
 *
 * The tracker seat is optional, so the token is looked for anywhere in the label rather
 * than at a fixed index. Anything unrecognised stays Real-Debrid: that is what every
 * candidate was before TorBox and what an unlabelled one still is, and a wrong bubble
 * is worse than a coarse one.
 */
export function sourceOf(c: Candidate): SourceProvider {
  const parts = (c.originLabel ?? '').split('·').map((s) => s.trim().toUpperCase());
  if (parts.includes('TORBOX') || parts.includes('TB')) return TORBOX;
  if (parts.includes('PREMIUMIZE') || parts.includes('PM')) return PREMIUMIZE;
  return REAL_DEBRID;
}
